// chatContext/chatBlockThread.ts
//
// One companion chat for a whole QBank block (ORA-1285). Each block the
// student opens has a scope: `question` (each question has its own chat, as
// before) or `block` (one thread follows the student through the block). It is
// resolved once per block per page session:
//
//   1. the server has a thread for this block: `block`;
//   2. else the student's default (`chatBlockThreadDefault`) is on and they did
//      not pick "This question" for this block in this browser session: `block`;
//   3. else `question`.
//
// Step 1 is the drawer's own thread lookup. While a block resolves, its thread
// key is the block's, so the anchor lookup that would show the block thread
// anyway also answers whether there is one (`useChatBlockThread`).
//
// A module store (ora-fe ADR-0003): the drawer contents write it, and the
// composer pill, the continue offer, the thread's dividers, the document
// viewer's thread key and `sendChatTurn` read it, with no provider in common.

import { useSyncExternalStore } from 'react';
import { registerChatSessionClearer } from '../slices/chat/chatSessionLifecycle';

export type ChatBlockScope = 'question' | 'block';

/**
 * A block's scope once the drawer has resolved it; absent until then.
 * `ineligible` once the server refused the block (404): from then on it behaves
 * as if block chats did not exist.
 */
export type ChatBlockScopeEntry = ChatBlockScope | 'ineligible';

/** The line a scope change leaves in the thread, after one of its messages. */
export interface ChatBlockScopeNotice {
  afterMessageId: string;
  scope: ChatBlockScope;
}

/** The QBank block the student is in, with the question on screen (1-based). */
export interface ChatBlockContext {
  blockId: string;
  index: number;
  total: number;
}

/** "Continue with Ora from question N?" (ORA-1285), shown above the composer. */
export interface ChatBlockOffer {
  blockId: string;
  /** The question the offer was made on; it lapses when the student moves. */
  atIndex: number;
  fromIndex: number;
  fromConversationId: string;
}

export interface ChatBlockThreadState {
  current: ChatBlockContext | null;
  scopes: Readonly<Record<string, ChatBlockScopeEntry>>;
  /**
   * The line each conversation shows where its scope changed ("This chat now
   * covers the whole block."). Client-side only, so it lasts for this page.
   */
  notices: Readonly<Record<string, ChatBlockScopeNotice>>;
  offer: ChatBlockOffer | null;
}

const EMPTY: ChatBlockThreadState = {
  current: null,
  scopes: {},
  notices: {},
  offer: null,
};

let state: ChatBlockThreadState = EMPTY;
const listeners = new Set<() => void>();

const setState = (next: ChatBlockThreadState) => {
  state = next;
  listeners.forEach((listener) => listener());
};

export const getChatBlockThreadState = (): ChatBlockThreadState => state;

export const subscribeChatBlockThread = (
  listener: () => void
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useChatBlockThreadState = (): ChatBlockThreadState =>
  useSyncExternalStore(
    subscribeChatBlockThread,
    getChatBlockThreadState,
    getChatBlockThreadState
  );

const isSameContext = (
  a: ChatBlockContext | null,
  b: ChatBlockContext | null
): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.blockId === b.blockId &&
    a.index === b.index &&
    a.total === b.total);

/** Called by the drawer contents whenever the block or the question changes. */
export const setCurrentChatBlock = (context: ChatBlockContext | null): void => {
  if (isSameContext(state.current, context)) return;

  // The offer belongs to the question it was made on.
  const offer =
    state.offer &&
    context?.blockId === state.offer.blockId &&
    context.index === state.offer.atIndex
      ? state.offer
      : null;

  setState({ ...state, current: context, offer });
};

/** Undefined before the block's resolution has started. */
export const getChatBlockScopeEntry = (
  blockId: string
): ChatBlockScopeEntry | undefined => state.scopes[blockId];

export const setChatBlockScopeEntry = (
  blockId: string,
  entry: ChatBlockScopeEntry
): void => {
  if (state.scopes[blockId] === entry) return;

  setState({
    ...state,
    scopes: { ...state.scopes, [blockId]: entry },
    // A block chat has nothing to offer to continue.
    offer: state.offer?.blockId === blockId ? null : state.offer,
  });
};

/**
 * The block whose thread a surface with this context follows, or null for the
 * per-question thread. A block not resolved yet follows its block key: that
 * lookup is the resolution.
 */
export const blockThreadIdFor = (
  context: ChatBlockContext | null,
  entry: ChatBlockScopeEntry | undefined
): string | null =>
  context && (entry === undefined || entry === 'block')
    ? context.blockId
    : null;

/** The block thread the current block follows, for readers outside React. */
export const currentChatBlockThreadId = (): string | null =>
  blockThreadIdFor(
    state.current,
    state.current ? state.scopes[state.current.blockId] : undefined
  );

export const addChatBlockScopeNotice = (
  conversationId: string,
  notice: ChatBlockScopeNotice
): void => {
  setState({
    ...state,
    notices: { ...state.notices, [conversationId]: notice },
  });
};

export const setChatBlockOffer = (offer: ChatBlockOffer | null): void => {
  if (state.offer === offer) return;
  setState({ ...state, offer });
};

/**
 * The last question of each block whose chat the drawer saw, for the continue
 * offer. Not state anyone renders, so it does not notify.
 */
export interface ChatBlockQuestionSeen {
  /**
   * The question's own thread key. "The student moved" is read off this, not
   * the index: the study slice moves the index a render before the page
   * publishes the next question's anchor, so for one render the index is the
   * new question's and everything else the old one's.
   */
  questionKey: string;
  /** Its 1-based position, as it was when the question's key first showed. */
  index: number;
  conversationId: string | null;
  hasUserMessage: boolean;
}

const questionsSeen = new Map<string, ChatBlockQuestionSeen>();

export const getChatBlockQuestionSeen = (
  blockId: string
): ChatBlockQuestionSeen | undefined => questionsSeen.get(blockId);

export const setChatBlockQuestionSeen = (
  blockId: string,
  seen: ChatBlockQuestionSeen
): void => {
  questionsSeen.set(blockId, seen);
};

// Per browser session (sessionStorage), so a reload keeps the student's
// choices but a new tab or a new day does not.
const STORAGE_PREFIX = 'ora.chatBlockThread.';

const questionChoiceKey = (blockId: string) =>
  `${STORAGE_PREFIX}question.${blockId}`;

const offerShownKey = (blockId: string) => `${STORAGE_PREFIX}offer.${blockId}`;
const OFFER_DISMISSED_KEY = `${STORAGE_PREFIX}offerDismissed`;

const readFlag = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (key: string, value: boolean): void => {
  try {
    if (value) window.sessionStorage.setItem(key, '1');
    else window.sessionStorage.removeItem(key);
  } catch {
    // Storage can be unavailable (private mode, a full quota). The choice then
    // lasts until the page reloads, which is an acceptable loss.
  }
};

/** The student picked "This question" for this block in this session. */
export const hasChosenQuestionScope = (blockId: string): boolean =>
  readFlag(questionChoiceKey(blockId));

export const rememberQuestionScopeChoice = (
  blockId: string,
  chosen: boolean
): void => writeFlag(questionChoiceKey(blockId), chosen);

/** The continue offer is shown at most once per block. */
export const wasChatBlockOfferShown = (blockId: string): boolean =>
  readFlag(offerShownKey(blockId));

export const markChatBlockOfferShown = (blockId: string): void =>
  writeFlag(offerShownKey(blockId), true);

/** After its ✕, the offer does not come back in this session. */
export const isChatBlockOfferDismissed = (): boolean =>
  readFlag(OFFER_DISMISSED_KEY);

export const markChatBlockOfferDismissed = (): void =>
  writeFlag(OFFER_DISMISSED_KEY, true);

const clearSessionFlags = (): void => {
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Nothing stored, then.
  }
};

/** Everything here belongs to the signed-in account. */
export const resetChatBlockThreadState = (): void => {
  questionsSeen.clear();
  clearSessionFlags();
  setState(EMPTY);
};

registerChatSessionClearer(resetChatBlockThreadState);
